'use client';

import { useMemo, useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Title1,
  Title2,
  Subtitle1,
  Subtitle2,
  Body1,
  Caption1,
  Button,
  Card,
  Badge,
  Link,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Lightbulb20Regular,
  Organization20Regular,
  Chat20Regular,
  CheckmarkCircle20Regular,
  ArrowRight20Regular,
  Building20Regular,
  News20Regular,
  DocumentText20Regular,
  Shield20Regular,
  Search20Regular,
  ArrowRight16Regular,
} from '@fluentui/react-icons';
import { useRouter } from 'next/navigation';
import { NexusThemeProvider } from '@/theme/provider';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { ReactFlowProvider } from 'reactflow';
import {
  convertToReactFlowNodes,
  convertToReactFlowEdges,
  hierarchicalLayout,
} from '@/lib/reactflowConfig';
import {
  EXAMPLE_GRAPH_NODES,
  EXAMPLE_GRAPH_EDGES,
} from '@/lib/exampleGraphData';
import { EntityNode } from '@/components/graph/EntityNode';

const nodeTypes = { entity: EntityNode };

/* ─────────── styles ─────────── */
const useStyles = makeStyles({
  page: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
  },

  /* ── Navbar ── */
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    backgroundColor: 'rgba(11,45,53,0.85)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  navLogo: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
    color: '#6BC7D3',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 0,
    letterSpacing: '-0.5px',
  },
  navActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },

  /* ── Hero ── */
  hero: {
    position: 'relative',
    minHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: `120px ${tokens.spacingHorizontalXXL} 80px`,
    background: 'linear-gradient(170deg, #091B1F 0%, #0B2D35 35%, #0C3D47 65%, #0B2D35 100%)',
    overflow: 'hidden',
  },
  heroContent: {
    position: 'relative',
    zIndex: 2,
    maxWidth: '820px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
  },
  heroBadge: {
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
  },
  heroTitle: {
    fontSize: '3.8rem',
    lineHeight: 1.08,
    fontWeight: tokens.fontWeightBold,
    color: '#FFFFFF',
    letterSpacing: '-1.5px',
  },
  heroGradientText: {
    background: 'linear-gradient(135deg, #6BC7D3, #A6E6EB, #6BC7D3)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroTagline: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: tokens.fontSizeBase400,
    maxWidth: '580px',
    lineHeight: 1.7,
  },
  heroActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: tokens.spacingVerticalS,
  },
  heroSvg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: 0,
  },
  heroBottomFade: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '180px',
    background: `linear-gradient(to top, ${tokens.colorNeutralBackground1}, transparent)`,
    zIndex: 1,
  },

  /* ── Stats bar ── */
  statsBar: {
    position: 'relative',
    zIndex: 3,
    marginTop: '-48px',
    maxWidth: '960px',
    width: '100%',
    marginInline: 'auto',
    padding: `0 ${tokens.spacingHorizontalXXL}`,
  },
  statsCard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 20px 50px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
    lineHeight: 1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },

  /* ── Section wrappers ── */
  section: {
    padding: `80px ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1120px',
    margin: '0 auto',
    width: '100%',
  },
  sectionHeader: {
    textAlign: 'center',
    marginBottom: '56px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
  },
  sectionLabel: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    fontSize: '12px',
  },
  sectionSubtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase400,
    maxWidth: '600px',
    lineHeight: 1.7,
  },

  /* ── Features (How it works) ── */
  threeCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalXL,
  },
  featureCard: {
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    textAlign: 'center',
    alignItems: 'center',
    borderRadius: tokens.borderRadiusXLarge,
  },
  featureIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '16px',
    background: 'linear-gradient(135deg, rgba(2,130,151,0.12), rgba(2,130,151,0.04))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  featureStep: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
  },

  /* ── Interactive graph demo ── */
  graphSection: {
    padding: `80px ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  graphContainer: {
    width: '100%',
    height: '520px',
    borderRadius: '16px',
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  graphCaption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalL,
    marginTop: tokens.spacingVerticalL,
    flexWrap: 'wrap',
  },
  legendDot: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },

  /* ── Chat demo ── */
  chatSection: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  chatSectionInner: {
    padding: `80px ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1120px',
    margin: '0 auto',
    width: '100%',
  },
  chatDemo: {
    maxWidth: '640px',
    margin: '0 auto',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    padding: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  chatDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#16A34A',
  },
  chatBubble: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: '18px',
    maxWidth: '85%',
    lineHeight: 1.5,
  },
  chatBubbleUser: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    alignSelf: 'flex-end',
    borderBottomRightRadius: '4px',
  },
  chatBubbleBot: {
    backgroundColor: tokens.colorNeutralBackground3,
    alignSelf: 'flex-start',
    borderBottomLeftRadius: '4px',
  },

  /* ── Use cases ── */
  useCasesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: tokens.spacingHorizontalL,
  },
  useCaseCard: {
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusXLarge,
  },
  useCaseIconWrap: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalXS,
  },

  /* ── Pricing ── */
  pricingSection: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  pricingSectionInner: {
    padding: `80px ${tokens.spacingHorizontalXXL}`,
    maxWidth: '1120px',
    margin: '0 auto',
    width: '100%',
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
  },
  pricingCard: {
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusXLarge,
  },
  pricingCardFeatured: {
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusXLarge,
    border: `2px solid ${tokens.colorBrandBackground}`,
    boxShadow: '0 8px 32px rgba(2,130,151,0.12)',
  },
  price: {
    fontSize: '3rem',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: 1,
    letterSpacing: '-1px',
  },
  priceSub: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    listStyle: 'none',
    padding: 0,
    margin: `${tokens.spacingVerticalS} 0`,
  },
  featureListItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground2,
  },

  /* ── CTA banner ── */
  ctaBanner: {
    background: 'linear-gradient(135deg, #0B2D35, #0C4D5A)',
    padding: '80px 32px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalL,
  },

  /* ── Footer ── */
  footer: {
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    maxWidth: '1120px',
    margin: '0 auto',
    width: '100%',
  },
  footerLinks: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },

  /* ── Additional utility classes ── */
  navLoginBtn: {
    color: 'rgba(255,255,255,0.75)',
  },
  heroBadgeColor: {
    color: '#6BC7D3',
    borderTopColor: 'rgba(107,199,211,0.3)',
    borderRightColor: 'rgba(107,199,211,0.3)',
    borderBottomColor: 'rgba(107,199,211,0.3)',
    borderLeftColor: 'rgba(107,199,211,0.3)',
  },
  heroOutlineBtn: {
    color: 'rgba(255,255,255,0.85)',
    borderTopColor: 'rgba(255,255,255,0.2)',
    borderRightColor: 'rgba(255,255,255,0.2)',
    borderBottomColor: 'rgba(255,255,255,0.2)',
    borderLeftColor: 'rgba(255,255,255,0.2)',
  },
  featureBody: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
    lineHeight: 1.6,
  },
  chatOnline: {
    color: tokens.colorNeutralForeground3,
  },
  chatMessages: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  chatUserText: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
  sourceLink: {
    fontSize: '12px',
  },
  useCaseBody: {
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.6,
  },
  selfStart: {
    alignSelf: 'flex-start',
  },
  subtleCaption: {
    color: tokens.colorNeutralForeground3,
  },
  checkBrand: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  pricingCta: {
    marginTop: 'auto',
  },
  ctaTitle: {
    color: '#FFFFFF',
  },
  ctaSubtext: {
    color: 'rgba(255,255,255,0.65)',
    maxWidth: '480px',
    textAlign: 'center',
    lineHeight: 1.7,
  },
  footerCaption: {
    color: tokens.colorNeutralForeground3,
  },
  footerLink: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  legendDotCircle: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
  },
});

/* ─────────── Hero background network ─────────── */
const HERO_NODES = [
  { x: 50, y: 46, r: 7, color: '#6BC7D3', delay: 0 },
  { x: 30, y: 28, r: 5, color: '#3B82F6', delay: 0.4 },
  { x: 68, y: 22, r: 5, color: '#D97706', delay: 0.9 },
  { x: 74, y: 62, r: 5, color: '#16A34A', delay: 1.4 },
  { x: 26, y: 66, r: 5, color: '#DC2626', delay: 1.9 },
  { x: 14, y: 18, r: 3.5, color: '#9333EA', delay: 0.2 },
  { x: 50, y: 10, r: 3.5, color: '#EA580C', delay: 0.7 },
  { x: 86, y: 32, r: 3.5, color: '#6366F1', delay: 1.2 },
  { x: 88, y: 72, r: 3.5, color: '#10B981', delay: 1.7 },
  { x: 54, y: 84, r: 3.5, color: '#EC4899', delay: 2.2 },
  { x: 10, y: 76, r: 3.5, color: '#F59E0B', delay: 0.1 },
  { x: 82, y: 14, r: 2.5, color: '#8B5CF6', delay: 0.6 },
  { x: 5, y: 44, r: 2.5, color: '#06B6D4', delay: 1.1 },
  { x: 95, y: 52, r: 2.5, color: '#3B82F6', delay: 1.6 },
  { x: 38, y: 88, r: 2.5, color: '#D97706', delay: 2.1 },
  { x: 76, y: 86, r: 2.5, color: '#16A34A', delay: 0.3 },
  { x: 18, y: 8, r: 2, color: '#EF4444', delay: 0.8 },
  { x: 62, y: 4, r: 2, color: '#7C3AED', delay: 1.3 },
  { x: 42, y: 38, r: 3, color: '#028297', delay: 0.5 },
  { x: 58, y: 58, r: 3, color: '#028297', delay: 1.0 },
];

const HERO_EDGES: [number, number][] = [
  [0, 1], [0, 2], [0, 3], [0, 4], [0, 18], [0, 19],
  [1, 5], [1, 6], [1, 18],
  [2, 7], [2, 11], [2, 6],
  [3, 8], [3, 15], [3, 19],
  [4, 10], [4, 9], [4, 12],
  [5, 16], [6, 17], [7, 13],
  [8, 15], [10, 12], [9, 14],
  [18, 19], [1, 4], [2, 3],
];

function HeroGraphBackground() {
  const styles = useStyles();
  return (
    <svg className={styles.heroSvg} aria-hidden="true" preserveAspectRatio="none" viewBox="0 0 100 100">
      {/* Radial glow at center */}
      <defs>
        <radialGradient id="heroGlow" cx="50%" cy="46%" r="35%">
          <stop offset="0%" stopColor="#028297" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#028297" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="100" height="100" fill="url(#heroGlow)" />

      {/* Edges */}
      {HERO_EDGES.map(([a, b], i) => (
        <line
          key={`e${i}`}
          x1={HERO_NODES[a].x}
          y1={HERO_NODES[a].y}
          x2={HERO_NODES[b].x}
          y2={HERO_NODES[b].y}
          stroke="rgba(107,199,211,0.12)"
          strokeWidth="0.2"
          className="hero-edge-animated"
        />
      ))}

      {/* Nodes */}
      {HERO_NODES.map((n, i) => (
        <circle
          key={`n${i}`}
          cx={n.x}
          cy={n.y}
          r={n.r * 0.15}
          fill={n.color}
          opacity="0.45"
          className="hero-pulse"
          style={{ animationDelay: `${n.delay}s` }}
        />
      ))}
    </svg>
  );
}

/* ─────────── Interactive graph demo ─────────── */
function InteractiveGraphDemo() {
  const styles = useStyles();
  const rfNodes = useMemo(() => {
    const raw = convertToReactFlowNodes(EXAMPLE_GRAPH_NODES);
    const edges = convertToReactFlowEdges(EXAMPLE_GRAPH_EDGES);
    return hierarchicalLayout(raw, edges);
  }, []);
  const rfEdges = useMemo(() => convertToReactFlowEdges(EXAMPLE_GRAPH_EDGES), []);

  const legendItems = [
    { label: 'Person', color: '#028297' },
    { label: 'Organization', color: '#D97706' },
    { label: 'Contract', color: '#16A34A' },
    { label: 'Project', color: '#9333EA' },
    { label: 'Court Case', color: '#DC2626' },
    { label: 'Location', color: '#3B82F6' },
    { label: 'Audit', color: '#EF4444' },
  ];

  return (
    <section className={styles.graphSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionLabel}>Knowledge Graph</span>
        <Title2>See the connections others miss</Title2>
        <Text className={styles.sectionSubtitle}>
          Explore a live knowledge graph linking entities, contracts, and legal proceedings.
          Pan, zoom, and click to interact.
        </Text>
      </div>

      <div className={mergeClasses('graph-demo-container')}>
        <div className={styles.graphContainer}>
          <ReactFlowProvider>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.25 }}
              minZoom={0.3}
              maxZoom={1.5}
              proOptions={{ hideAttribution: true }}
            >
              <Background color={tokens.colorNeutralForeground4} gap={20} />
              <Controls showInteractive={false} />
              <MiniMap
                maskColor="rgba(0, 0, 0, 0.2)"
                style={{ backgroundColor: tokens.colorNeutralBackground2 }}
              />
            </ReactFlow>
          </ReactFlowProvider>
        </div>
      </div>

      <div className={styles.graphCaption}>
        {legendItems.map((item) => (
          <div key={item.label} className={styles.legendDot}>
            <span
              className={styles.legendDotCircle}
              style={{ backgroundColor: item.color }}
            />
            {item.label}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─────────── Data ─────────── */
const STATS = [
  { value: '124K+', label: 'Entities Indexed' },
  { value: '547K+', label: 'Relationships' },
  { value: '50+', label: 'Data Sources' },
  { value: '99.9%', label: 'Uptime' },
];

const HOW_IT_WORKS = [
  {
    step: 'Step 01',
    Icon: Search20Regular,
    title: 'Seed',
    body: 'Enter a name — a person, company, or contract. Nexus starts collecting from government records, legal databases, and news sources in real time.',
  },
  {
    step: 'Step 02',
    Icon: Organization20Regular,
    title: 'Connect',
    body: 'A living knowledge graph emerges — linking entities, surfacing hidden relationships, and flagging risk signals with confidence scores.',
  },
  {
    step: 'Step 03',
    Icon: Chat20Regular,
    title: 'Query',
    body: 'Ask anything in plain English. Get structured, cited answers with entity links and exportable intelligence reports.',
  },
];

const USE_CASES = [
  {
    Icon: News20Regular,
    title: 'Journalists',
    body: 'Follow the money. Trace corporate networks, beneficial owners, and public contract awards.',
    color: '#EA580C',
    bg: 'rgba(234,88,12,0.08)',
  },
  {
    Icon: DocumentText20Regular,
    title: 'Researchers',
    body: 'Build evidence maps across thousands of documents — automatically structured and cited.',
    color: '#9333EA',
    bg: 'rgba(147,51,234,0.08)',
  },
  {
    Icon: Building20Regular,
    title: 'Political Analysts',
    body: 'Profile constituencies, track government spending, and identify conflicts of interest.',
    color: '#028297',
    bg: 'rgba(2,130,151,0.08)',
  },
  {
    Icon: Shield20Regular,
    title: 'Due Diligence',
    body: 'Automated background checks across government, legal, and news sources.',
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.08)',
  },
];

const PRICING = [
  {
    tier: 'Free',
    price: '$0',
    period: '/month',
    desc: 'For individuals getting started.',
    features: ['10 queries per month', 'Basic entity search', 'View entity details', 'No reports or exports'],
    cta: 'Get started free',
    featured: false,
  },
  {
    tier: 'Researcher',
    price: '$49',
    period: '/month',
    desc: 'For investigators and analysts.',
    features: ['Unlimited queries', 'Entity seeding', 'Intelligence reports', 'Graph explorer (2-hop)', 'CSV export'],
    cta: 'Start free trial',
    featured: true,
  },
  {
    tier: 'Professional',
    price: '$149',
    period: '/month',
    desc: 'For teams and power users.',
    features: ['Everything in Researcher', 'Full graph exploration', 'Alerts & watchlists', 'PDF & DOCX export', 'API access', 'Team seats'],
    cta: 'Contact sales',
    featured: false,
  },
];

const FOOTER_LINKS = ['About', 'Blog', 'Privacy', 'Terms', 'Contact'];

/* ─────────── Main page ─────────── */
export default function LandingPage() {
  const styles = useStyles();
  const router = useRouter();

  return (
    <NexusThemeProvider>
      <div className={styles.page}>

        {/* ── Navbar ── */}
        <nav className={styles.nav}>
          <button className={styles.navLogo} aria-label="Nexus home">Nexus</button>
          <div className={styles.navActions}>
            <Button
              appearance="subtle"
              className={styles.navLoginBtn}
              onClick={() => router.push('/login')}
            >
              Log In
            </Button>
            <Button appearance="primary" onClick={() => router.push('/register')}>
              Get Started
            </Button>
          </div>
        </nav>

        {/* ── Hero ── */}
        <section className={styles.hero}>
          <HeroGraphBackground />
          <div className={styles.heroContent}>
            <Badge
              appearance="outline"
              size="large"
              className={mergeClasses(styles.heroBadge, 'hero-fade-in-up', styles.heroBadgeColor)}
            >
              Entity Intelligence Platform
            </Badge>

            <h1 className={mergeClasses(styles.heroTitle, 'hero-fade-in-up-delay-1')}>
              Intelligence at the{' '}
              <span className={styles.heroGradientText}>Speed&nbsp;of&nbsp;Search</span>
            </h1>

            <Text className={mergeClasses(styles.heroTagline, 'hero-fade-in-up-delay-2')}>
              Nexus maps the hidden web of relationships across public records,
              court filings, and news — giving you the intelligence to act with confidence.
            </Text>

            <div className={mergeClasses(styles.heroActions, 'hero-fade-in-up-delay-3')}>
              <Button
                size="large"
                appearance="primary"
                iconPosition="after"
                icon={<ArrowRight20Regular />}
                onClick={() => router.push('/register')}
              >
                Start free
              </Button>
              <Button
                size="large"
                appearance="outline"
                className={styles.heroOutlineBtn}
                onClick={() => router.push('/login')}
              >
                Watch demo
              </Button>
            </div>
          </div>
          <div className={styles.heroBottomFade} />
        </section>

        {/* ── Stats bar ── */}
        <div className={mergeClasses(styles.statsBar, 'hero-fade-in-up-delay-4')}>
          <div className={mergeClasses(styles.statsCard, 'landing-stats-grid')}>
            {STATS.map((s) => (
              <div key={s.label} className={styles.statItem}>
                <span className={styles.statNumber}>{s.value}</span>
                <span className={styles.statLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── How it works ── */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>How It Works</span>
            <Title2>From a name to a full intelligence picture</Title2>
            <Text className={styles.sectionSubtitle}>
              Three steps to unlock the relationships that matter most.
            </Text>
          </div>
          <div className={mergeClasses(styles.threeCol, 'landing-three-col')}>
            {HOW_IT_WORKS.map(({ step, Icon, title, body }) => (
              <Card key={title} className={mergeClasses(styles.featureCard, 'landing-feature-card')}>
                <span className={styles.featureStep}>{step}</span>
                <div className={styles.featureIcon}>
                  <Icon fontSize={26} />
                </div>
                <Subtitle2>{title}</Subtitle2>
                <Body1 className={styles.featureBody}>
                  {body}
                </Body1>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Interactive Graph Demo ── */}
        <InteractiveGraphDemo />

        {/* ── Chat demo ── */}
        <div className={styles.chatSection}>
          <div className={styles.chatSectionInner}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionLabel}>Natural Language AI</span>
              <Title2>Ask anything. Get structured answers.</Title2>
              <Text className={styles.sectionSubtitle}>
                Natural language queries backed by a live knowledge graph and primary sources.
              </Text>
            </div>
            <div className={styles.chatDemo}>
              <div className={styles.chatHeader}>
                <div className={styles.chatDot} />
                <Caption1 className={styles.chatOnline}>Nexus AI — Online</Caption1>
              </div>
              <div className={styles.chatMessages}>
                <div className={mergeClasses(styles.chatBubble, styles.chatBubbleUser)}>
                  <Text className={styles.chatUserText}>
                    Who are the directors of ABC Construction Ltd and what contracts have they won?
                  </Text>
                </div>
                <div className={mergeClasses(styles.chatBubble, styles.chatBubbleBot)}>
                  <Text>
                    ABC Construction Ltd has two directors: <strong>James Mwangi</strong> and{' '}
                    <strong>Sarah Ochieng</strong>. The company has won 14 government contracts since
                    2019 totaling KES 8.7B, primarily from KeNHA. Note: OAG flagged KES 340M in
                    questioned costs in the 2023 audit.{' '}
                    <Link href="#" className={styles.sourceLink}>[Source 1]</Link>{' '}
                    <Link href="#" className={styles.sourceLink}>[Source 2]</Link>
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Use cases ── */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>Use Cases</span>
            <Title2>Built for serious investigation</Title2>
          </div>
          <div className={mergeClasses(styles.useCasesGrid, 'landing-four-col')}>
            {USE_CASES.map(({ Icon, title, body, color, bg }) => (
              <Card key={title} className={mergeClasses(styles.useCaseCard, 'landing-feature-card')}>
                <div className={styles.useCaseIconWrap} style={{ backgroundColor: bg }}>
                  <Icon style={{ color }} fontSize={22} />
                </div>
                <Subtitle2>{title}</Subtitle2>
                <Caption1 className={styles.useCaseBody}>{body}</Caption1>
              </Card>
            ))}
          </div>
        </section>

        {/* ── Pricing ── */}
        <div className={styles.pricingSection}>
          <div className={styles.pricingSectionInner}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionLabel}>Pricing</span>
              <Title2>Simple, transparent pricing</Title2>
              <Text className={styles.sectionSubtitle}>Start free. Scale when you need more.</Text>
            </div>
            <div className={mergeClasses(styles.pricingGrid, 'landing-three-col')}>
              {PRICING.map((plan) => (
                <Card
                  key={plan.tier}
                  className={mergeClasses(
                    plan.featured ? styles.pricingCardFeatured : styles.pricingCard,
                    'landing-pricing-card',
                  )}
                >
                  {plan.featured && (
                    <Badge appearance="filled" color="brand" className={styles.selfStart}>
                      Most Popular
                    </Badge>
                  )}
                  <Subtitle1>{plan.tier}</Subtitle1>
                  <div>
                    <span className={styles.price}>{plan.price}</span>
                    <span className={styles.priceSub}>{plan.period}</span>
                  </div>
                  <Caption1 className={styles.subtleCaption}>{plan.desc}</Caption1>
                  <ul className={styles.featureList}>
                    {plan.features.map((f) => (
                      <li key={f} className={styles.featureListItem}>
                        <CheckmarkCircle20Regular
                          className={styles.checkBrand}
                        />
                        <Caption1>{f}</Caption1>
                      </li>
                    ))}
                  </ul>
                  <Button
                    appearance={plan.featured ? 'primary' : 'outline'}
                    onClick={() => router.push('/register')}
                    className={styles.pricingCta}
                  >
                    {plan.cta}
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* ── CTA banner ── */}
        <section className={styles.ctaBanner}>
          <Title2 className={styles.ctaTitle}>Ready to uncover the connections?</Title2>
          <Text className={styles.ctaSubtext}>
            Join thousands of analysts, journalists, and investigators already using Nexus to surface the truth.
          </Text>
          <Button
            size="large"
            appearance="primary"
            iconPosition="after"
            icon={<ArrowRight16Regular />}
            onClick={() => router.push('/register')}
          >
            Get started for free
          </Button>
        </section>

        {/* ── Footer ── */}
        <footer className={styles.footer}>
          <Caption1 className={styles.footerCaption}>
            &copy; {new Date().getFullYear()} Nexus Entity Intelligence Platform
          </Caption1>
          <div className={styles.footerLinks}>
            {FOOTER_LINKS.map((l) => (
              <Link key={l} href="#" className={styles.footerLink}>
                {l}
              </Link>
            ))}
          </div>
        </footer>
      </div>
    </NexusThemeProvider>
  );
}
